// Pages built from translated content: the bots pillar, freelancer use cases, bot
// integrations, comparisons, glossary terms and the desktop and phone apps. The words live in
// public/locales/<lang>/<ns>.json under the page's key; this file only says where
// each page is, what it links to and which picture it shows.
//
// Content shape (per key): seo_title, seo_description, eyebrow, title, subtitle,
// answer (the 40-60 word reply an assistant can quote), blocks[] and faq[].
// Block types: text {h, p[]}, list {h, intro, items[]}, steps {h, items[{t, d}]},
// cards {h, items[{t, d}]}, table {h, cols[], rows[[]], note}, handoff {h, items[{you, bot}]}.

export const GUIDES_UPDATED = '2026-10-30';

export const GUIDE_GROUPS = {
  bots:     { ns: 'bots',     hub: '/ai-bots' },
  usecases: { ns: 'usecases', hub: '/use-cases' },
  apps:     { ns: 'apps',     hub: '/integrations' },
  compare:  { ns: 'compare',  hub: '/alternatives' },
  glossary: { ns: 'glossary', hub: '/glossary' },
  download: { ns: 'download', hub: '/download' },
};

const bot = (slug, icon, image, related) => ({ group: 'bots', key: slug.replace(/-/g, '_'), path: `/ai-bots/${slug}`, icon, image, related });
const role = (slug, icon, related) => ({ group: 'usecases', key: slug.replace(/-/g, '_'), path: `/use-cases/${slug}`, icon, related });
const app = (slug, logo, related) => ({ group: 'apps', key: slug, path: `/integrations/${slug}`, logo: `/integrations/${logo}`, related });
// updated: when the page's facts were last checked, when it is not GUIDES_UPDATED.
const rival = (slug, related, updated) => ({ group: 'compare', key: slug.replace(/-/g, '_'), path: `/alternatives/${slug}`, icon: 'arrow-right-left', related, ...(updated ? { updated } : {}) });
const term = (slug, related) => ({ group: 'glossary', key: slug.replace(/-/g, '_'), path: `/glossary/${slug}`, icon: 'book', related });
const device = (slug, icon, image, related) => ({ group: 'download', key: slug, path: `/download/${slug}`, icon, image, related });

const BOT_PAGES = [
  bot('busybee', 'bot', '/screenshots/bots/busybee.webp', ['/ai-bots/chat', '/ai-bots/approvals', '/ai-bots/computer']),
  bot('chat', 'message-square', '/screenshots/bots/chat.webp', ['/ai-bots/busybee', '/ai-bots/tasks', '/ai-bots/memory']),
  bot('tasks', 'list-checks', '/screenshots/bots/task-review.webp', ['/ai-bots/approvals', '/ai-bots/schedules', '/use-cases']),
  bot('computer', 'monitor', '/screenshots/bots/computer.webp', ['/ai-bots/approvals', '/glossary/computer-use', '/integrations/github']),
  bot('memory', 'brain', '/screenshots/bots/memory.webp', ['/ai-bots/chat', '/glossary/agent-memory', '/ai-bots/approvals']),
  bot('schedules', 'calendar-clock', '/screenshots/bots/schedules.webp', ['/ai-bots/tasks', '/ai-bots/models', '/use-cases/consultants']),
  bot('connected-apps', 'plug', '/screenshots/bots/apps.webp', ['/integrations/notion', '/integrations/github', '/glossary/mcp']),
  bot('approvals', 'shield-check', '/screenshots/bots/approval.webp', ['/glossary/human-in-the-loop', '/glossary/prompt-injection', '/ai-bots/connected-apps']),
  bot('models', 'cpu', '/screenshots/bots/models.webp', ['/glossary/ai-credits', '/glossary/byok', '/pricing']),
];

const ROLE_PAGES = [
  role('designers', 'pencil', ['/integrations/higgsfield', '/integrations/fal', '/ai-bots/connected-apps']),
  role('developers', 'code', ['/integrations/github', '/ai-bots/computer', '/integrations/cloudflare']),
  role('marketers', 'megaphone', ['/ai-bots/schedules', '/integrations/higgsfield', '/ai-bots/computer']),
  role('writers', 'file-text', ['/ai-bots/memory', '/integrations/notion', '/ai-bots/chat']),
  role('consultants', 'briefcase', ['/ai-bots/schedules', '/integrations/stripe', '/ai-bots/tasks']),
  role('virtual-assistants', 'inbox', ['/ai-bots/approvals', '/integrations/clickup', '/ai-bots/tasks']),
  role('content-creators', 'video', ['/integrations/higgsfield', '/integrations/fal', '/ai-bots/computer']),
  role('solo-founders', 'rocket', ['/integrations/stripe', '/integrations/github', '/ai-bots/schedules']),
];

// Canva is still offered only to BusyBee.Day's admins in the bots' app catalog (admins_only in
// backend/agents/mcp_connections.py), so its page stays off the site (App.js redirects it) until
// everyone can connect it.
export const HIDDEN_APPS = ['canva'];

const APP_PAGES = [
  app('notion', 'notion.svg', ['/alternatives/notion', '/ai-bots/connected-apps', '/use-cases/writers']),
  app('clickup', 'clickup.svg', ['/alternatives/clickup', '/ai-bots/connected-apps', '/use-cases/virtual-assistants']),
  app('canva', 'canva.svg', ['/use-cases/designers', '/use-cases/marketers', '/ai-bots/approvals']),
  app('higgsfield', 'higgsfield.svg', ['/integrations/fal', '/use-cases/content-creators', '/ai-bots/models']),
  app('fal', 'fal.png', ['/integrations/higgsfield', '/use-cases/content-creators', '/ai-bots/computer']),
  app('github', 'github.svg', ['/use-cases/developers', '/ai-bots/computer', '/integrations/cloudflare']),
  app('cloudflare', 'cloudflare.svg', ['/integrations/github', '/use-cases/developers', '/ai-bots/approvals']),
  app('stripe', 'stripe.svg', ['/integrations/paypal', '/use-cases/solo-founders', '/ai-bots/approvals']),
  app('paypal', 'paypal.svg', ['/integrations/stripe', '/use-cases/consultants', '/ai-bots/approvals']),
].filter((g) => !HIDDEN_APPS.includes(g.key));

const COMPARE_PAGES = [
  rival('chatgpt-dots', ['/alternatives/manus', '/ai-bots/computer', '/ai-bots/models']),
  rival('notion', ['/integrations/notion', '/alternatives/clickup', '/ai-bots/tasks']),
  rival('clickup', ['/integrations/clickup', '/alternatives/asana', '/ai-bots/tasks']),
  rival('asana', ['/alternatives/clickup', '/alternatives/trello', '/ai-bots/tasks']),
  rival('motion', ['/alternatives/sunsama', '/ai-bots/schedules', '/features/calendar']),
  rival('lindy', ['/alternatives/manus', '/ai-bots/connected-apps', '/glossary/ai-credits']),
  rival('todoist', ['/alternatives/trello', '/alternatives/sunsama', '/ai-bots/tasks'], '2026-11-02'),
  rival('trello', ['/alternatives/asana', '/alternatives/todoist', '/ai-bots/tasks'], '2026-11-02'),
  rival('manus', ['/alternatives/chatgpt-dots', '/ai-bots/computer', '/glossary/computer-use'], '2026-11-02'),
  rival('sunsama', ['/alternatives/motion', '/alternatives/todoist', '/features/calendar'], '2026-11-02'),
];

const TERM_PAGES = [
  term('ai-agent', ['/ai-bots', '/glossary/human-in-the-loop', '/glossary/agent-memory']),
  term('mcp', ['/ai-bots/connected-apps', '/integrations/mcp', '/glossary/ai-agent']),
  term('human-in-the-loop', ['/ai-bots/approvals', '/glossary/prompt-injection', '/glossary/ai-agent']),
  term('agent-memory', ['/ai-bots/memory', '/glossary/ai-agent', '/glossary/mcp']),
  term('computer-use', ['/ai-bots/computer', '/glossary/prompt-injection', '/glossary/ai-agent']),
  term('prompt-injection', ['/ai-bots/approvals', '/glossary/human-in-the-loop', '/glossary/computer-use']),
  term('ai-credits', ['/ai-bots/models', '/glossary/byok', '/pricing']),
  term('byok', ['/ai-bots/models', '/glossary/ai-credits', '/pricing']),
];

// The apps for computers and phones. Neither is public yet: the pages say so until the
// store listings and installers are live.
const DOWNLOAD_PAGES = [
  device('desktop', 'monitor', '/screenshots/desktop/drop.webp', ['/download/mobile', '/ai-bots/approvals', '/ai-bots/busybee']),
  device('mobile', 'smartphone', '/screenshots/mobile/phones.webp', ['/download/desktop', '/ai-bots/approvals', '/ai-bots/chat']),
];

export const GUIDES = [
  { group: 'bots', key: 'index', path: '/ai-bots', icon: 'bot', image: '/screenshots/bots/hub.webp',
    children: BOT_PAGES.map((g) => g.path), related: ['/use-cases', '/alternatives/chatgpt-dots', '/pricing'] },
  ...BOT_PAGES,
  { group: 'usecases', key: 'index', path: '/use-cases', icon: 'users',
    children: ROLE_PAGES.map((g) => g.path), related: ['/ai-bots', '/ai-bots/tasks', '/pricing'] },
  ...ROLE_PAGES,
  ...APP_PAGES,
  ...COMPARE_PAGES,
  ...TERM_PAGES,
  { group: 'download', key: 'index', path: '/download', icon: 'monitor',
    children: DOWNLOAD_PAGES.map((g) => g.path), related: ['/ai-bots/busybee', '/ai-bots/approvals', '/pricing'] },
  ...DOWNLOAD_PAGES,
];

const BY_PATH = Object.fromEntries(GUIDES.map((g) => [g.path, g]));

export const getGuide = (path) => BY_PATH[(path || '').replace(/\/+$/, '') || '/'];
export const guidesIn = (group) => GUIDES.filter((g) => g.group === group && g.key !== 'index');
export const APP_GUIDES = APP_PAGES;
export const COMPARE_GUIDES = COMPARE_PAGES;
export const TERM_GUIDES = TERM_PAGES;
export const BOT_GUIDES = BOT_PAGES;
export const ROLE_GUIDES = ROLE_PAGES;
export const DOWNLOAD_GUIDES = DOWNLOAD_PAGES;
